import { Statistic } from 'antd'
import isNil from 'lodash-es/isNil'
import isUndefined from 'lodash-es/isUndefined'
import React, { FunctionComponent } from 'react'
import styled from 'styled-components'
import { Duration } from '@/components/common/duration-select'
import useKolDetailRoute from '@/components/kol/hooks/use-kol-detail-route'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Nullable } from '@/types/nullable'
import { Optional } from '@/types/optional'
import { KpiSchema } from '@/types/schema/kpi-schema'
import { PlatformShortcode } from '@/utils/convert-platform'

interface NumberSummaryProps {
  isLoading?: boolean
  kpi?: KpiSchema
}

const NumberSummary: FunctionComponent<NumberSummaryProps> = ({
  isLoading,
  kpi,
}) => {
  const { formatMessage, formatNumber } = useIntl()
  const { searchParams } = useKolDetailRoute()

  const viewRate = ((): Optional<Nullable<number>> => {
    if (!kpi) {
      return undefined
    }

    if (
      kpi.platform === PlatformShortcode.Instagram ||
      kpi.platform === PlatformShortcode.TikTok
    ) {
      return searchParams.data?.duration === Duration.ThreeMonths
        ? kpi.avgShortPlayRate3m
        : kpi.avgShortPlayRate6m
    }

    return searchParams.data?.duration === Duration.ThreeMonths
      ? kpi.avgPvRate3m
      : kpi.avgPvRate6m
  })()
  const interactiveRate =
    searchParams.data?.duration === Duration.ThreeMonths
      ? kpi?.interactiveRate3m
      : kpi?.interactiveRate6m
  const isNoFollowerData = isUndefined(kpi) || kpi.follower === 0
  const isNoViewRateData = isNil(viewRate) || viewRate === 0
  const isNoEngagementRateData = isNil(interactiveRate) || interactiveRate === 0

  return (
    <Container>
      <StatisticWrapper isNoData={isNoFollowerData}>
        <StyledStatistic
          loading={isLoading}
          title={formatMessage({ id: 'general:column_title_follower_count' })}
          value={
            isNoFollowerData
              ? formatMessage({ id: 'autoreport:chart_label_empty_data' })
              : kpi?.follower
          }
        />
      </StatisticWrapper>
      <StatisticWrapper isNoData={isNoEngagementRateData}>
        <StyledStatistic
          loading={isLoading}
          title={formatMessage({ id: 'general:column_title_engagement_rate' })}
          value={
            isNoEngagementRateData
              ? formatMessage({ id: 'autoreport:chart_label_empty_data' })
              : formatNumber(interactiveRate, {
                  isPercentageDisplay: true,
                  maximumFractionDigits: 2,
                })
          }
        />
      </StatisticWrapper>
      <StatisticWrapper isNoData={isNoViewRateData}>
        <StyledStatistic
          loading={isLoading}
          title={formatMessage({ id: 'general:column_title_view_rate' })}
          value={
            isNoViewRateData
              ? formatMessage({ id: 'autoreport:chart_label_empty_data' })
              : formatNumber(viewRate, {
                  isPercentageDisplay: true,
                  maximumFractionDigits: 2,
                })
          }
        />
      </StatisticWrapper>
    </Container>
  )
}

const Container = styled.div`
  display: none;
  padding-top: 8px;
  padding-bottom: 8px;

  @media (min-width: 981px) {
    display: flex;
  }
`

const StatisticWrapper = styled.div<{ isNoData: boolean }>`
  .ant-statistic {
    text-align: center;
    font-style: normal;
    line-height: 140%;
    padding: 0 12px;
    min-width: 100px;
  }

  .ant-statistic-title {
    color: ${({ theme }): string => theme.colors.base.grey.grey2};
    font-size: 13px;
    font-weight: 400;
    text-wrap: nowrap;
  }

  .ant-statistic-content {
    color: ${({ theme, isNoData }): string =>
      isNoData ? theme.colors.base.grey.grey4 : theme.colors.base.grey.grey1};
    font-size: 18px;
    font-weight: ${({ isNoData }): number => (isNoData ? 400 : 600)};
  }
`

const StyledStatistic = styled(Statistic)`
  .ant-statistic-skeleton {
    padding-top: 0 !important;

    .ant-skeleton-title {
      margin-bottom: 0 !important;
    }
  }
`

export default NumberSummary
